import {
  StyleSheet,
  Text,
  View,
  Image,
  Share,
  Linking,
  Platform,
} from 'react-native';
import React, { useContext, useMemo } from 'react';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { TouchableRipple } from 'react-native-paper';
import ErrorContext from '../../../Context/ErrorContext';
import { isTablet } from 'react-native-device-info';
import { fs, iconSize, v, s } from '../../../utils/libs';
import { getApiMessage } from '../../../utils/apiError';
import ReviewService from '../../../services/ReviewService';
import { appAnalytics } from '../../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../../services/appAnalyticsContext';
import { useTheme, withOpacity, GTFonts } from '../../../theme';
import { useSecureNavigate } from '../../security/useSecureNavigate';
import { IOS_APPSTORE_ID } from '../../../CONSTANTS';

const BIZPLUS_DOWNLOADS_URL = 'https://bizpluspos.co.ke/downloads';
const BIZPLUS_ANDROID_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.muriuki.bizpluspos&hl=en&gl=US';
const BIZPLUS_IOS_STORE_URL = IOS_APPSTORE_ID
  ? `https://apps.apple.com/app/id${IOS_APPSTORE_ID}`
  : BIZPLUS_DOWNLOADS_URL;
const BIZPLUS_SHARE_MESSAGE =
  'Hey there! BizPlus has effortlessly automated how I manage my businesses. Download it now and revolutionize your operations too.';

const CustomDrawer = ({ navigation }: { navigation: any }) => {
  const { handleError } = useContext(ErrorContext);
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const { secureNavigate } = useSecureNavigate(navigation);

  const ShareApp = async () => {
    try {
      if (Platform.OS === 'web') {
        const nav = globalThis.navigator as any;
        if (typeof nav?.share === 'function') {
          await nav.share({
            title: 'BizPlus POS',
            text: BIZPLUS_SHARE_MESSAGE,
            url: BIZPLUS_DOWNLOADS_URL,
          });
          return;
        }

        await nav?.clipboard?.writeText?.(BIZPLUS_DOWNLOADS_URL);
        handleError('BizPlus download link copied.', 'success', true);
        return;
      }

      if (Platform.OS === 'ios') {
        await Share.share({
          title: 'BizPlus POS',
          message: `${BIZPLUS_SHARE_MESSAGE} ${BIZPLUS_IOS_STORE_URL}`,
          url: BIZPLUS_IOS_STORE_URL,
        });
        return;
      }

      await Share.share({
        title: 'App link',
        message: `${BIZPLUS_SHARE_MESSAGE} ${BIZPLUS_ANDROID_STORE_URL}`,
        url: BIZPLUS_ANDROID_STORE_URL,
      });
    } catch (error) {
      if ((error as any)?.name === 'AbortError') {
        return;
      }
      if (error instanceof Error) {
        handleError(getApiMessage(error), 'error', true);
      }
    }
  };

  const openInAppBrowser = async () => {
    await secureNavigate('Browser');
  };

  const DrawerItemRow = ({
    iconName,
    label,
    onPress,
  }: {
    iconName: React.ComponentProps<typeof MaterialCommunityIcons>['name'];
    label: string;
    onPress: () => void;
  }) => (
    <TouchableRipple
      onPress={onPress}
      rippleColor={withOpacity(theme.colors.primary, 0.12)}
      style={styles.draweritem}
      borderless={false}>
      <View style={styles.row}>
        <View style={styles.leftRow}>
          <MaterialCommunityIcons
            name={iconName}
            size={iconSize(22)}
            color={theme.colors.primaryDark}
            style={styles.itemIcon}
          />
          <Text style={styles.drawertext}>{label}</Text>
        </View>
        <MaterialCommunityIcons
          name="chevron-right"
          size={iconSize(20)}
          color={theme.colors.textSecondary}
        />
      </View>
    </TouchableRipple>
  );

  return (
    <View style={{ flex: 1 }}>
      <View style={{ flex: 1 }}>
        <View
          style={{
            marginBottom: v(20),
            backgroundColor: theme.colors.primaryDark,
          }}>
          <View
            style={{
              height: v(120),
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: v(15),
              marginTop: v(40),
            }}>
            <Image
              source={require('../../../assets/BizplusLogo.webp')}
              resizeMode="contain"
              style={{
                width: isTablet() ? '100%' : '75%',
                height: v(90),
              }}
            />
          </View>
        </View>
        <View>
          <DrawerItemRow
            iconName="home-account"
            label="About BizPlus POS"
            onPress={() => Linking.openURL('https://www.bizpluspos.co.ke')}
          />
          <DrawerItemRow
            iconName="frequently-asked-questions"
            label="Frequently Asked Questions"
            onPress={() => navigation.navigate('FAQScreen')}
          />
          <DrawerItemRow
            iconName="message-text-outline"
            label="Feedback & Suggestions"
            onPress={() => {
              navigation.closeDrawer?.();
              requestAnimationFrame(() => navigation.navigate('Suggestion'));
            }}
          />
          <DrawerItemRow
            iconName="star"
            label="Rate Us"
            onPress={async () => {
              navigation.closeDrawer?.();
              appAnalytics.track('review_cta_tapped', {
                ...buildAppAnalyticsContext(),
                source: 'sidebar',
              });
              // Show modal first, then open store directly if accepted
              await ReviewService.requestStoreWithModal('sidebar');
            }}
          />
        </View>
      </View>
      <View style={{ marginBottom: v(10) }}>
        <DrawerItemRow
          iconName="share-variant"
          label="Share with Friends"
          onPress={ShareApp}
        />
        <DrawerItemRow
          iconName="shield-lock-outline"
          label="Privacy Policy"
          onPress={() =>
            Linking.openURL(
              'https://archive.org/details/bizplus-point-of-sale-privacy-policy',
            )
          }
        />
      </View>
    </View>
  );
};

export default CustomDrawer;

const createStyles = (theme: any) =>
  StyleSheet.create({
    draweritem: {
      marginBottom: v(16),
      paddingHorizontal: s(16),
      height: v(36),
      ...(Platform.OS === 'web'
        ? {
            height: v(48),
            paddingVertical: v(16),
          }
        : {
            // height: v(36),
          }),
      justifyContent: 'center',
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    leftRow: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    itemIcon: {
      marginRight: s(10),
    },
    drawertext: {
      fontSize: fs(16),
      lineHeight: fs(20),
      marginTop: 0,
      fontFamily: GTFonts.regular,
      color: theme.colors.text,
    },
  });
